/**
 * Namespace `account`: organização, assinatura e suporte.
 *
 * Cobre `src/components/organization/`, `src/components/billing/`,
 * `src/components/support/`, `src/hooks/usePlanLimits.ts` e o catálogo de
 * features em `src/utils/featureConfig.ts`.
 *
 * Os textos foram RECORTADOS dos componentes sem reescrita. Onde a tela já
 * estava sem acento ("Voce", "Descricao", "opcao"), o dicionário repete o erro:
 * corrigir aqui seria mudar produto no meio de uma tradução, e o inglês e o
 * espanhol saem corretos de qualquer forma.
 *
 * O que NÃO veio para cá, por ser dado e não rótulo:
 *  - `featureKey` / id de feature: casa com o array `features` do plano no banco;
 *  - nome de plano ('free', 'starter', 'professional', 'business') e o
 *    `displayName` deles: identificador do banco e do Stripe;
 *  - papel de organização ('owner' | 'admin' | 'member') e de projeto
 *    ('viewer' | 'editor' | 'admin'): vão para colunas do banco;
 *  - status e categoria de ticket ('open', 'waiting_user', 'bug'...): idem;
 *  - códigos de erro do serviço ('invite_expired', 'FEATURE_NOT_AVAILABLE',
 *    '23505'): são comparados com `includes`/`===`.
 * Em todos esses casos a CHAVE do dicionário é o próprio valor persistido.
 */
const account = {
  /** `src/components/organization/` inteiro. */
  org: {
    /**
     * Papel do membro na organização. A chave é o valor da coluna `role`.
     * "Admin" é o mesmo nos três idiomas.
     */
    roles: {
      owner: 'Proprietário',
      admin: 'Admin',
      member: 'Membro',
    },

    /** OrgSwitcher: o seletor "Pessoal / organizações" da sidebar. */
    switcher: {
      personal: 'Pessoal',
      create: 'Criar Organização',
      /** "Starter+" é nome de plano e fica igual nos três idiomas. */
      createLocked: 'Criar Organização (Starter+)',
    },

    /** OrgCreateModal. */
    create: {
      title: 'Criar Organização',
      subtitle: 'Organize sua equipe e compartilhe projetos',

      nameLabel: 'Nome da Organização',
      namePlaceholder: 'Ex: Minha Empresa',

      slugLabel: 'Identificador (slug)',
      slugPlaceholder: 'minha-empresa',
      slugHint: 'Apenas letras minúsculas, números e hífens',
      slugAvailable: 'Disponível',
      slugUnavailable: 'Já em uso',

      descriptionLabel: 'Descrição (opcional)',
      descriptionPlaceholder: 'Uma breve descrição da organização...',

      nameRequired: 'Nome é obrigatório',
      slugTooShort: 'Slug deve ter pelo menos 2 caracteres',
      slugTaken: 'Este slug já está em uso',
      slugTakenTryAnother: 'Este slug já está em uso. Tente outro.',
      featureNotAvailable: 'Sua assinatura não permite criar organizações. Faça upgrade para Starter ou superior.',
      created: 'Organização criada com sucesso!',
      createError: 'Erro ao criar organização',

      cancel: 'Cancelar',
      creating: 'Criando...',
      submit: 'Criar Organização',
    },

    /** OrgInviteModal: convidar alguém por email. */
    invite: {
      title: 'Convidar Membro',
      /** `{{org}}` = nome da organização atual. */
      subtitle: 'Convide para {{org}}',

      emailLabel: 'Email',
      emailPlaceholder: 'colega@empresa.com',
      roleLabel: 'Papel',
      /**
       * Linha de apoio de cada papel no seletor. O NOME do papel não está aqui:
       * vem de `org.roles`, para o gatilho do select mostrar só "Membro" /
       * "Admin" e a explicação viver dentro do menu. A chave é o valor gravado
       * na coluna `role`, o mesmo de `org.roles`.
       */
      roleHints: {
        member: 'Pode ver os projetos da organização',
        admin: 'Pode gerenciar membros e configurações',
      },
      hint: 'Um email com o link de convite será enviado. Se o usuário não tiver conta, poderá criar uma ao aceitar o convite.',

      invalidEmail: 'Informe um email válido',
      memberLimit: 'Limite de membros atingido. Faça upgrade do plano.',
      alreadyPending: 'Já existe um convite pendente para este email.',
      sendError: 'Erro ao enviar convite',
      /** `{{email}}` = destinatário do convite. */
      sent: 'Convite enviado para {{email}}',
      createdNoEmail: 'Convite criado para {{email}}, mas falha no envio do email. Compartilhe o link manualmente.',

      cancel: 'Cancelar',
      sending: 'Enviando...',
      submit: 'Enviar Convite',
    },

    /** OrgInviteBanner: os convites pendentes no topo da sidebar. */
    inviteBanner: {
      /** Nome genérico quando o convite chega sem a organização carregada. */
      orgFallback: 'organização',
      /** `{{org}}` = nome da organização (ou o fallback acima). */
      inviteFor: 'Convite para {{org}}',
      accept: 'Aceitar',
      decline: 'Recusar',

      joined: 'Você agora faz parte de {{org}}',
      acceptError: 'Erro ao aceitar convite',
      expired: 'Este convite expirou.',
      memberLimit: 'A organização atingiu o limite de membros.',
      declineError: 'Erro ao recusar convite',
    },

    /** OrgInviteAcceptPage: a tela que abre pelo link do email. */
    inviteAccept: {
      processing: 'Processando convite...',
      alreadyMember: 'Você já é membro!',
      welcome: 'Bem-vindo à organização!',
      redirecting: 'Redirecionando para o MeuGantt...',

      errorTitle: 'Não foi possível aceitar o convite',
      unknownError: 'Erro desconhecido',
      notFound: 'Convite não encontrado ou já utilizado.',
      expired: 'Este convite expirou. Solicite um novo convite.',
      memberLimit: 'A organização atingiu o limite de membros do plano.',

      goToApp: 'Ir para o MeuGantt',
    },

    /** OrgMembersPanel: lista de membros e convites pendentes. */
    members: {
      title: 'Membros',
      /** `{{value}}` é "3/10" ou o texto de ilimitado abaixo. */
      count: '{{value}} membros',
      unlimited: 'ilimitado',
      invite: 'Convidar',
      limitReached: 'Limite de membros atingido. Faça upgrade do plano para adicionar mais.',

      /** Nome exibido quando o membro não tem nome nem email carregados. */
      userFallback: 'Usuário',
      you: '(você)',

      promote: 'Promover a Admin',
      demote: 'Rebaixar para Membro',
      remove: 'Remover',
      /** Nome usado na confirmação quando o membro não tem nome nem email. */
      memberFallback: 'membro',
      /** `{{name}}` = nome (ou email) do membro. */
      confirmRemove: 'Remover {{name}} da organização?',
      removed: '{{name}} removido da organização',
      removeError: 'Erro ao remover membro',
      roleUpdated: 'Papel atualizado',
      roleUpdateError: 'Erro ao atualizar papel',

      /** `{{count}}` = quantos convites ainda estão pendentes. */
      pendingInvites: 'Convites Pendentes ({{count}})',
      /** `{{date}}` já vem formatado por `fmt.date`. */
      expiresOn: 'Expira em {{date}}',
      pending: 'Pendente',
      cancelInvite: 'Cancelar convite',
      inviteCanceled: 'Convite cancelado',
      cancelInviteError: 'Erro ao cancelar convite',
    },

    /** OrgSettingsPanel: a aba Organização das Configurações. */
    settings: {
      emptyTitle: 'Nenhuma organização selecionada',
      emptyBody: 'Selecione uma organização no seletor da sidebar ou crie uma nova para gerenciar equipes e compartilhar projetos.',

      infoSection: 'Informações',
      nameLabel: 'Nome',
      descriptionLabel: 'Descrição',
      descriptionPlaceholder: 'Uma breve descrição da organização...',
      slugLabel: 'Identificador (slug)',
      slugHint: 'O slug não pode ser alterado após a criação',

      saving: 'Salvando...',
      save: 'Salvar Alterações',
      updated: 'Organização atualizada',
      updateError: 'Erro ao atualizar organização',

      dangerSection: 'Zona de Perigo',
      dangerBody: 'Ao excluir a organização, todos os projetos dela voltarão a ser projetos pessoais do respectivo dono. Os membros perderão acesso aos projetos que tinham via organização.',
      deleting: 'Excluindo...',
      deleteButton: 'Excluir Organização',
      /** `{{name}}` = nome exato que o usuário precisa digitar para confirmar. */
      confirmDeletePrompt: 'Para excluir "{{name}}", digite o nome da organização:',
      nameMismatch: 'Nome não corresponde. Exclusão cancelada.',
      deleted: 'Organização excluída. Projetos foram mantidos como pessoais.',
      deleteError: 'Erro ao excluir organização',
    },

    /** OrgProjectShareModal: mover um projeto pessoal para a organização. */
    projectShare: {
      title: 'Mover para Organização',

      orgLabel: 'Organização',
      orgPlaceholder: 'Selecione uma organização',
      /** `{{name}}` = nome da org, `{{members}}` = a contagem já pluralizada. */
      orgOption: '{{name}} ({{members}})',
      memberCount: { one: '{{count}} membro', other: '{{count}} membros' },

      shareWithMembers: 'Compartilhar com todos os membros da organização',
      defaultRoleLabel: 'Papel padrão dos membros no projeto',
      /**
       * Papel no PROJETO (não na organização). A chave é o valor gravado em
       * `project_members.role`.
       */
      roleOptions: {
        viewer: 'Visualizador - apenas leitura',
        editor: 'Editor - pode editar tarefas',
        admin: 'Admin - pode gerenciar projeto',
      },

      addAsResources: 'Adicionar membros como recursos do projeto',
      weeklyHours: 'Horas semanais por recurso',

      cancel: 'Cancelar',
      moving: 'Movendo...',
      submit: 'Mover Projeto',

      /** `{{count}}` = quantos membros receberam acesso ao projeto. */
      movedAndShared: {
        one: 'Projeto movido e compartilhado com {{count}} membro',
        other: 'Projeto movido e compartilhado com {{count}} membros',
      },
      moved: 'Projeto movido para a organização',
      moveError: 'Erro ao mover projeto',
    },
  },

  /** `src/components/billing/` e os limites de plano. */
  billing: {
    /** Valor mostrado onde o limite do plano é -1. */
    unlimited: 'Ilimitado',

    /**
     * Rótulo curto de cada feature, indexado pela chave que vem do banco.
     * Consumido por `usePlanLimits`.
     */
    planFeatures: {
      gantt_basic: 'Gantt Básico',
      gantt_dependencies: 'Dependências no Gantt',
      calendar: 'Calendário',
      timeline: 'Cronograma/Timeline',
      todos: 'Pendências',
      risks: 'Riscos',
      changes: 'Mudanças',
      costs: 'Custos',
      export_csv: 'Exportar CSV',
      export_csv_limited: 'Exportar CSV (1/mês)',
      export_excel: 'Exportar Excel',
      export_pdf: 'Exportar PDF',
      export_png: 'Exportar PNG',
      share_public: 'Compartilhar (público)',
      share_password: 'Compartilhar (com senha)',
      api_access: 'Acesso à API',
    },

    /** UpgradeOverlay: o cartaz que cobre um módulo bloqueado. */
    upgrade: {
      defaultTitle: 'Funcionalidade Premium',
      defaultBody: 'Esta funcionalidade não está disponível no seu plano atual.',
      /**
       * Frase partida porque o nome do plano vai em negrito no meio dela. Em
       * português o plano fecha a frase, então `availablePost` fica vazio; em
       * inglês ele carrega o "plan" final.
       */
      availablePre: 'Disponível no plano ',
      availablePost: '',
      upgrade: 'Fazer Upgrade',
    },

    /** TrialBanner: o aviso de teste gratuito na sidebar. */
    trialBanner: {
      /** Versões curtas, usadas no tooltip da sidebar recolhida. */
      expiredShort: 'Trial expirado',
      expiresTodayShort: 'Trial expira hoje!',
      daysShort: { one: 'Trial — {{count}} dia', other: 'Trial — {{count}} dias' },

      ctaUpgrade: 'Fazer upgrade',
      ctaSubscribeNow: 'Assinar agora',
      ctaSubscribe: 'Assinar',

      /** "Professional" é nome de plano e não se traduz. */
      titleActive: 'Trial Professional',
      titleExpired: 'Trial Expirado',
      expiresToday: 'Expira hoje!',
      daysRemaining: { one: '{{count}} dia restante', other: '{{count}} dias restantes' },
      expiredBody: 'Seus dados foram preservados. Faça upgrade para recuperar o acesso.',
    },

    /**
     * TrialExpiredModal. O original está sem acento na tela ("periodo",
     * "Voce", "Faca"); o recorte manteve exatamente como está.
     */
    trialExpired: {
      title: 'Seu periodo de teste expirou',
      /** Frase partida: "preservados" vai em negrito no meio. */
      bodyPre: 'Voce agora esta no plano Free com ate 3 projetos. Seus dados e projetos foram ',
      bodyStrong: 'preservados',
      bodyPost: ' — nada foi perdido. Faca upgrade para recuperar o acesso completo a todos os recursos.',
      cta: 'Ver planos e assinar',
      dismiss: 'Continuar no plano Free',
    },

    /**
     * PricingPage. O nome comercial e a descrição de cada plano vêm do banco
     * (`plans.display_name` e `plans.description`) e não passam por aqui.
     * Preço e moeda continuam como o Stripe cobra: BRL nos três idiomas.
     */
    pricing: {
      title: 'Escolha seu plano',
      subtitle: 'Comece grátis e faça upgrade quando precisar de mais recursos',

      monthly: 'Mensal',
      yearly: 'Anual',
      /** Desconto do plano anual; o número é fixo na arte do botão. */
      discountBadge: '-25%',

      mostPopular: 'Mais Popular',
      free: 'Grátis',
      perUserMonth: '/usuário/mês',
      /** `{{pct}}` = economia calculada entre o mensal e o anual. */
      savings: 'Economia de {{pct}}%',

      currentPlan: 'Plano Atual',
      startFree: 'Começar Grátis',
      subscribe: 'Assinar',
      /** `{{count}}` = dias de teste vindos do parâmetro `?trial=`. */
      trialCta: { one: 'Teste grátis por {{count}} dia', other: 'Teste grátis por {{count}} dias' },

      stripeUnconfigured: 'Stripe não configurado para este plano. Entre em contato com o suporte.',
      checkoutError: 'Erro ao processar. Tente novamente.',

      footerSupport: 'Todos os planos incluem suporte por email e atualizações automáticas',
      footerContact: 'Dúvidas? Entre em contato: suporte@meugantt.com.br',

      /**
       * Itens da lista de cada plano, indexados por um id local. O componente
       * guarda só a ORDEM e o `included` (com ou sem check), porque isso é
       * regra de produto, não texto.
       */
      features: {
        projects3: 'Até 3 projetos',
        projects10: 'Até 10 projetos',
        projectsUnlimited: 'Projetos ilimitados',
        allFeatures: 'Todas as features',
        ganttBasic: 'Gantt básico',
        ganttDependencies: 'Gantt com dependências',
        kanban: 'Kanban',
        kanbanSprints: 'Kanban com Sprints',
        calendar: 'Calendário',
        calendarTimeline: 'Calendário e Timeline',
        exportCsvLimited: 'Exportar CSV (1/mês)',
        exportCsvUnlimited: 'Exportar CSV ilimitado',
        exportPdfPng: 'Exportar PDF/PNG',
        templates: 'Templates de Projeto',
        baseline: 'Baseline de Cronograma',
        organizations: 'Organizações',
        organizations10: 'Organizações (até 10 membros)',
        organizationsUnlimited: 'Organizações (membros ilimitados)',
        todos: 'Pendências (Todos)',
        dependencies: 'Dependências',
        raci: 'Matriz RACI',
        wbs: 'EAP (Estrutura Analítica)',
        criticalPath: 'Caminho Crítico',
        portfolio: 'Portfólio de Projetos',
        capacity: 'Planejamento de Capacidade',
        risksChanges: 'Riscos e Mudanças',
        costs: 'Custos',
        sharePublic: 'Compartilhamento público',
        sharePassword: 'Compartilhamento com senha',
        apiAccess: 'Acesso à API',
      },
    },
  },

  /**
   * Catálogo de features de `src/utils/featureConfig.ts`.
   *
   * A CHAVE é o `featureKey` que casa com o array `features` do plano no banco
   * e nunca muda com o idioma. Só o nome e a descrição mostrados na tela estão
   * aqui.
   */
  features: {
    gantt_basic: {
      name: 'Gantt Básico',
      description: 'Visualize seu projeto com gráfico de Gantt',
    },
    calendar: {
      name: 'Calendário',
      description: 'Visualize tarefas no calendário mensal',
    },
    timeline: {
      name: 'Cronograma',
      description: 'Visualize seu projeto em formato de cronograma semanal',
    },
    todos: {
      name: 'Pendências',
      description: 'Gerencie pendências e demandas do projeto',
    },
    risks: {
      name: 'Riscos',
      description: 'Gerencie riscos do projeto com matriz de probabilidade e impacto',
    },
    changes: {
      name: 'Mudanças',
      description: 'Acompanhe solicitações de mudança no projeto',
    },
    costs: {
      name: 'Custos',
      description: 'Controle custos e orçamento do projeto',
    },
    raci_matrix: {
      name: 'Matriz RACI',
      description: 'Defina responsabilidades com a matriz RACI',
    },
    eap_wbs: {
      name: 'EAP (Estrutura Analítica)',
      description: 'Visualize a decomposição hierárquica do projeto em diagrama de árvore',
    },
    gantt_dependencies: {
      name: 'Dependências no Gantt',
      description: 'Crie dependências entre tarefas no gráfico de Gantt',
    },
    export_csv_limited: {
      name: 'Exportar CSV (1/mês)',
      description: 'Exporte suas tarefas em CSV com limite mensal',
    },
    export_csv: {
      name: 'Exportar CSV',
      description: 'Exporte suas tarefas em CSV sem limites',
    },
    export_excel: {
      name: 'Exportar Excel',
      description: 'Baixe a planilha do projeto com tarefas, baseline, equipe e curva S',
    },
    export_pdf: {
      name: 'Exportar PDF',
      description: 'Exporte o gráfico de Gantt em formato PDF',
    },
    export_png: {
      name: 'Exportar Imagem',
      description: 'Exporte o gráfico de Gantt como imagem PNG',
    },
    share_public: {
      name: 'Compartilhamento Público',
      description: 'Compartilhe seu projeto com um link público',
    },
    share_password: {
      name: 'Proteção com Senha',
      description: 'Proteja o compartilhamento com senha de acesso',
    },
    api_access: {
      name: 'Acesso à API',
      description: 'Acesse seus dados via API REST',
    },
    portfolio: {
      name: 'Portfólio',
      description: 'Visão executiva de todos os projetos com KPIs, riscos e custos consolidados',
    },
    capacity_planning: {
      name: 'Planejamento de Capacidade',
      description: 'Gerencie capacidade e alocação de recursos em todos os projetos',
    },
    ai_project_creation: {
      name: 'Criação de Projeto com IA',
      description: 'Gere planos de projeto completos com inteligência artificial',
    },
    comments: {
      name: 'Comentários e @Menções',
      description: 'Adicione comentários nas tarefas e mencione membros da equipe',
    },
    organizations: {
      name: 'Organizações',
      description: 'Crie organizações e gerencie equipes para compartilhar projetos',
    },
    import_external: {
      name: 'Importar de Outros Sistemas',
      description: 'Importe projetos do Trello, ClickUp, Azure DevOps e MS Project',
    },
    multi_assignee: {
      name: 'Múltiplos Responsáveis',
      description: 'Atribua múltiplos membros da equipe a cada tarefa',
    },
    project_templates: {
      name: 'Templates de Projeto',
      description: 'Crie projetos a partir de modelos prontos por indústria',
    },
    schedule_baseline: {
      name: 'Baseline de Cronograma',
      description: 'Salve e compare o plano original com o cronograma atual',
    },
    critical_path: {
      name: 'Caminho Crítico',
      description: 'Identifique a sequência de tarefas que determina a duração mínima do projeto',
    },
    team_workload: {
      name: 'Carga de Trabalho da Equipe',
      description: 'Visualize a carga de trabalho automática baseada nas tarefas atribuídas',
    },
    capacity_alerts: {
      name: 'Alertas de Capacidade',
      description: 'Receba alertas quando membros da equipe estiverem sobrecarregados',
    },
    priority_support: {
      name: 'Suporte Prioritário',
      description: 'Tickets com prioridade elevada e tempo de resposta reduzido',
    },
  },

  /**
   * `src/components/support/`. Aqui também o original está sem acento em vários
   * pontos ("Voce", "Descricao", "avaliacao"); o recorte não corrigiu.
   */
  support: {
    /**
     * Status do ticket. A chave é o valor da coluna `status`, comparado no
     * componente e gravado no banco.
     */
    statuses: {
      open: 'Aberto',
      in_progress: 'Em Andamento',
      waiting_user: 'Aguardando Voce',
      resolved: 'Resolvido',
      closed: 'Fechado',
    },

    /** Categoria do ticket. A chave é o valor da coluna `category`. */
    categories: {
      bug: 'Bug',
      feature_request: 'Sugestao',
      question: 'Duvida',
      feedback: 'Feedback',
    },

    /** Prioridade do ticket, definida pelo plano. A chave vai para o banco. */
    priorities: {
      low: 'Baixa',
      medium: 'Media',
      high: 'Alta',
      urgent: 'Urgente',
    },

    /** SupportPanel: o menu inicial e os títulos de cada tela. */
    panel: {
      menuTitle: 'Central de Suporte',
      newTitle: 'Novo Ticket',
      listTitle: 'Meus Tickets',
      /** `{{number}}` = número sequencial do ticket. */
      ticketTitle: 'Ticket #{{number}}',

      menuIntro: 'Como podemos ajudar? Escolha uma opcao abaixo.',
      newItem: 'Novo Ticket',
      newItemHint: 'Reportar bug, sugerir funcionalidade ou tirar duvida',
      listItem: 'Meus Tickets',
      listItemHint: 'Acompanhar tickets abertos e resolvidos',
    },

    /** TicketForm: abrir um ticket novo. */
    form: {
      categoryLabel: 'Categoria',
      subjectLabel: 'Assunto',
      subjectPlaceholder: 'Descreva brevemente o problema ou sugestao...',
      messageLabel: 'Descricao',
      messagePlaceholder: 'Descreva com mais detalhes. Inclua passos para reproduzir o problema, se aplicavel...',
      attach: 'Anexar imagem ou PDF (max 10MB)',
      invalidFiles: 'Apenas imagens (PNG, JPG, GIF, WebP) e PDF ate 10MB.',

      cancel: 'Cancelar',
      sending: 'Enviando...',
      submit: 'Enviar Ticket',
      created: 'Ticket criado com sucesso! Responderemos em breve.',
      createError: 'Erro ao criar ticket. Tente novamente.',
    },

    /** TicketList: os tickets do usuário. */
    list: {
      /** Rótulo do filtro, indexado pelo valor que vai para o serviço. */
      filters: {
        all: 'Todos',
        open: 'Abertos',
        resolved: 'Resolvidos',
      },
      newButton: 'Novo',

      emptyTitle: 'Nenhum ticket encontrado',
      emptyAll: 'Voce ainda nao abriu nenhum ticket.',
      emptyOpen: 'Nenhum ticket aberto.',
      emptyResolved: 'Nenhum ticket resolvido.',
      createButton: 'Criar Ticket',

      /**
       * Idade do ticket em formato compacto, para caber ao lado do título.
       * `min`, `h` e `d` são abreviações e ficam iguais nos três idiomas.
       */
      justNow: 'agora',
      minutesAgo: '{{count}}min',
      hoursAgo: '{{count}}h',
      daysAgo: '{{count}}d',
    },

    /** TicketConversation: a thread de mensagens de um ticket. */
    conversation: {
      /** `{{value}}` = o rótulo da prioridade, de `support.priorities`. */
      priority: 'Prioridade: {{value}}',
      /** Nome do remetente quando a mensagem não tem autor identificado. */
      supportSender: 'Suporte',
      youSender: 'Voce',

      replyPlaceholder: 'Digite sua mensagem...',
      closedNotice: 'Este ticket foi fechado.',
      /** `{{count}}` = estrelas que o usuário deu ao atendimento. */
      rated: {
        one: 'Voce avaliou este atendimento com {{count}} estrela',
        other: 'Voce avaliou este atendimento com {{count}} estrelas',
      },

      loadError: 'Erro ao carregar ticket.',
      sendError: 'Erro ao enviar mensagem.',
    },

    /** SatisfactionRating: as cinco estrelas no fim do atendimento. */
    rating: {
      title: 'Como foi nosso atendimento?',
      commentPlaceholder: 'Algum comentario adicional? (opcional)',
      submit: 'Enviar Avaliacao',
      thanks: 'Obrigado pelo feedback!',
      error: 'Erro ao enviar avaliacao.',
    },
  },
};

/** en e es derivam desta forma: chave faltando vira erro de compilação. */
export type accountCopy = typeof account;

export default account;
